import { ErrorBoundary, For, Show } from 'solid-js';
import { customShadowlessElement, type CorrectComponentType } from '@brytdesigns/web-component-core/utils';
import { correctElementType } from '@brytdesigns/web-component-core';

import { formatMoney, useCartRecommendations, type RecommendedProduct } from '@/lib/cart-recommendations';

type Props = {
  dataUrl: string;
  dataCartUrl: string;
  dataLimit: number;
  dataIntent: string;
  dataTitle: string;
};

const SkeletonCards = () => (
  <div class="flex gap-3">
    <For each={[1, 2, 3, 4]}>
      {() => (
        <div class="cart-rec-slide shrink-0 animate-pulse" style={{ 'min-width': '260px', 'max-width': '260px' }}>
          <div class="cart-rec-card grid grid-cols-[110px_1fr] gap-4 items-center">
            <div class="cart-rec-image rounded-lg bg-foreground/5 aspect-square" />
            <div class="cart-rec-info flex flex-col gap-2">
              <div class="cart-rec-info-text">
                <div class="h-3 bg-foreground/5 rounded w-3/4 mb-1" />
                <div class="h-3 bg-foreground/5 rounded w-1/2" />
              </div>
              <div class="cart-rec-action-compact">
                <div class="h-5 bg-foreground/5 rounded-full w-12" />
              </div>
            </div>
          </div>
        </div>
      )}
    </For>
  </div>
);

const Price = (props: { product: RecommendedProduct }) => (
  <div class="flex items-center gap-1.5">
    {props.product.compare_at_price && props.product.compare_at_price > props.product.price ? (
      <>
        <s class="text-xs text-foreground">{formatMoney(props.product.compare_at_price)}</s>
        <span class="text-sm text-primary">{formatMoney(props.product.price)}</span>
      </>
    ) : props.product.price_varies ? (
      <span class="text-sm text-foreground">
        {formatMoney(props.product.price_min)} - {formatMoney(props.product.price_max)}
      </span>
    ) : (
      <span class="text-sm text-foreground">{formatMoney(props.product.price)}</span>
    )}
  </div>
);

const ActionButton = (props: { product: RecommendedProduct; cartUrl: string }) =>
  props.product.variants.length === 1 ? (
    <form action={props.cartUrl} method="post">
      <input type="hidden" name="id" value={String(props.product.variants[0]?.id)} />
      <button
        type="submit"
        aria-label={`Add ${props.product.title} to cart`}
        class="mt-0.5 bg-primary text-xs rounded-lg px-3 py-0.5 cursor-pointer text-background hover:bg-foreground hover:text-background transition-all duration-150 w-max font-bold"
        innerHTML={`<add-to-cart-action form-state="initial" class="flex items-center justify-center"><span data-content>ADD</span><template data-state="adding"><i class="fa-solid fa-spinner-third motion-preset-spin text-xs"></i></template><template data-state="added"><i class="fa-solid fa-check text-xs"></i></template><template data-state="error"><i class="fa-solid fa-xmark text-xs text-red-600"></i></template></add-to-cart-action>`}
      />
    </form>
  ) : (
    <a
      href={props.product.url}
      aria-label={`View options for ${props.product.title}`}
      class="mt-0.5 text-xs font-bold border border-foreground/20 rounded-full px-3 py-0.5 cursor-pointer text-foreground hover:bg-foreground hover:text-background transition-all duration-150 w-max inline-block no-underline"
    >
      VIEW
    </a>
  );

const Card = (props: { product: RecommendedProduct; cartUrl: string }) => (
  <div class="keen-slider__slide cart-rec-slide min-w-[230px] max-w-[230px]">
    <div class="cart-rec-card grid grid-cols-[110px_1fr] gap-4 items-center color-paper">
      <a href={props.product.url} class="no-underline block relative">
        <div class="cart-rec-image rounded-lg overflow-hidden bg-background border border-foreground/10 aspect-square relative">
          {props.product.compare_at_price && props.product.compare_at_price > props.product.price && (
            <span class="cart-rec-badge absolute top-2 right-2 z-10 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              Sale
            </span>
          )}
          {props.product.featured_image && (
            <img
              src={props.product.featured_image}
              alt={props.product.title}
              class="w-full h-full object-cover"
              loading="lazy"
            />
          )}
        </div>
      </a>
      <div class="cart-rec-info flex flex-col gap-0.5">
        <div class="cart-rec-info-text">
          <a href={props.product.url} class="no-underline">
            <p class="text-sm font-bold line-clamp-2 mb-0 text-foreground">{props.product.title}</p>
          </a>
        </div>
        <div class="cart-rec-action-compact shrink-0 flex gap-3">
          <Price product={props.product} />
          <ActionButton product={props.product} cartUrl={props.cartUrl} />
        </div>
      </div>
    </div>
  </div>
);

const Component: CorrectComponentType<Props> = (props, { element }) => {
  const limit = props.dataLimit || 6;
  const intent = props.dataIntent || 'related';

  const { recommendations, isLoading, hasItems } = useCartRecommendations({
    element,
    baseUrl: props.dataUrl,
    cartUrl: props.dataCartUrl,
    limit,
    intent,
  });

  const sliderId = `cart-recs-slider-${Math.random().toString(36).slice(2, 8)}`;
  const sliderConfig = {
    mode: 'free-snap',
    loop: false,
    slides: { perView: 'auto', spacing: 12 },
  };

  return (
    <ErrorBoundary fallback={null}>
      <Show when={hasItems()}>
        <div class="">
          <div class="flex items-center justify-between ">
            <h3 class="text-xl text-heading font-bold font-body mt-0">{props.dataTitle}</h3>
            <Show when={!isLoading() && recommendations().length > 0}>
              <keen-slider-navigation-arrows
                attr:target={`#${sliderId}`}
                attr:is-hidden="true"
                class="keen-arrows flex gap-2 shrink-0"
              >
                <template prev-arrow>
                  <i class="fa-sharp fa-light fa-chevron-left" />
                </template>
                <template next-arrow>
                  <i class="fa-sharp fa-light fa-chevron-right" />
                </template>
              </keen-slider-navigation-arrows>
            </Show>
          </div>

          <Show when={!isLoading()} fallback={<SkeletonCards />}>
            <keen-slider id={sliderId} attr:config={JSON.stringify(sliderConfig)} class="overflow-visible! keen-slider">
              <For each={recommendations()}>{(product) => <Card product={product} cartUrl={props.dataCartUrl} />}</For>
            </keen-slider>
          </Show>
        </div>
      </Show>
    </ErrorBoundary>
  );
};

customShadowlessElement(
  'cart-recommendations',
  {
    dataUrl: '',
    dataCartUrl: '/cart/add',
    dataLimit: 6,
    dataIntent: 'related',
    dataTitle: 'You may also like',
  },
  correctElementType(Component),
);
